// The Chain as a graph, in a dialog: a vertical line on the left (lanes and
// stations), one row per step on the right, and how far the Case has come on
// top. From 640px of dialog each row shows a column of notes on its right
// (who it waits for, delegated, the due date).
//
// Promise:
// - The graph only reads. Pressing a row closes the dialog and opens that step
//   in place in the Case (`onSelect`).
// - Escape, the close button and the backdrop close it.
// - A title starts right of the lines and stations of its own row (each row
//   on its own, fixed: scrolling does not move it).
// - The current station's ring spreads twice when the graph opens, then stays
//   still (not while reduced motion is asked for).
import type { CaseDetail } from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import { useMemo } from "react";
import { useFormat } from "../me/profile";
import { usePhone } from "../shell/phone";
import { ICON_BUTTON } from "../ui/button";
import { GlassIconButton } from "../ui/glass";
import { Icon } from "../ui/icon";
import { Modal } from "../ui/modal";
import { useWaitName } from "./bits";
import {
  canvasHeight,
  edgeColor,
  edgePath,
  type Geo,
  geometry,
  type LaneRow,
  LINE_COLORS,
  laneLayout,
  laneX,
  rowY,
  titleLeft,
  titleSpans,
} from "./chain-graph-layout";

/**
 * The line colors, by the number `laneLayout` gives each line
 * (`--color-chain-0..6`). 0 and 5 are both blue (the layout keeps them apart
 * in a lane); the rest are told apart by hue. Light and dark come with the
 * tokens. Written out whole: Tailwind only keeps a theme token whose name it
 * finds in the sources, so a name put together at run time leaves the light
 * values out of the CSS (the lines and rings were not drawn).
 */
const LINE_VARS = [
  "var(--color-chain-0)",
  "var(--color-chain-1)",
  "var(--color-chain-2)",
  "var(--color-chain-3)",
  "var(--color-chain-4)",
  "var(--color-chain-5)",
  "var(--color-chain-6)",
] as const satisfies { length: typeof LINE_COLORS };
const lineColor = (n: number) => LINE_VARS[n % LINE_COLORS];

/** The station's inside: the panel's own ground, so a line seems to pass behind. */
const GROUND = "var(--color-surface)";

/** The button beside the Chain's heading that opens the graph. */
export function ChainGraphButton({ onClick }: { onClick(): void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={m.cases_chain_graph()}
      className="inline-flex h-[24px] w-[24px] flex-none cursor-pointer items-center justify-center rounded-[6px] text-faint hover:bg-hover hover:text-body"
    >
      <Icon kind="chain" />
    </button>
  );
}

export function ChainGraph({
  c,
  onClose,
  onSelect,
}: {
  c: CaseDetail;
  onClose(): void;
  /** A row was pressed: close and open that step in place. */
  onSelect(stepId: string): void;
}) {
  const format = useFormat();
  const waitName = useWaitName();
  const { rows, lanes, geo, height, lefts, byId } = useMemo(() => {
    const { rows, lanes } = laneLayout(c.steps);
    const geo = geometry();
    const spans = titleSpans(rows, geo);
    return {
      rows,
      lanes,
      geo,
      height: canvasHeight(rows.length, geo),
      lefts: rows.map((r) => {
        const y = rowY(r.index, geo);
        return titleLeft(spans, y - geo.rowH / 2, y + geo.rowH / 2);
      }),
      byId: new Map(rows.map((r) => [r.step.id, r])),
    };
  }, [c.steps]);
  const svgWidth = laneX(Math.max(lanes - 1, 0), geo) + 20;
  const done = c.steps.filter((s) => s.status !== "open").length;
  const total = c.steps.length;
  const still =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  const metaOf = (r: LaneRow) => {
    const s = r.step;
    const waiting = s.kind === "waiting";
    const due = waiting ? s.waitUntil : s.dueAt;
    return [
      waiting && s.waitTarget ? waitName(s.waitTarget) : null,
      s.ext?.agent ? m.cases_graph_delegated() : null,
      due ? m.cases_step_due({ date: format.relativeDay(due) }) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  };
  // On a phone: the whole screen, no header row, and the close button where
  // the Dock's round button is (the old web's, 2026-10-07).
  const phone = usePhone();
  return (
    <Modal
      open
      onClose={onClose}
      label={m.cases_chain_graph()}
      size={phone ? "full" : "wide"}
      className={phone ? "" : "flex max-h-[min(80vh,860px)] flex-col"}
    >
      {/* Undo the dialog's padding: the header and the rows go edge to edge. */}
      <div
        className={`flex min-h-0 flex-1 flex-col ${phone ? "pt-[max(env(safe-area-inset-top),16px)]" : "-m-5"}`}
      >
        {phone ? (
          <div
            className="fixed z-10"
            style={{
              right: 33,
              bottom: "calc(var(--dock-floor) + 5px)",
            }}
          >
            <GlassIconButton
              label={m.cases_panel_close()}
              onClick={onClose}
              width={49}
              height={48}
            >
              <Icon kind="close" size={18} />
            </GlassIconButton>
          </div>
        ) : (
          <header className="flex h-12 flex-none items-center gap-2 pr-3 pl-5">
            <span className="text-[13px] text-body-soft">
              {m.cases_chain_graph()}
            </span>
            <button
              type="button"
              aria-label={m.cases_panel_close()}
              onClick={onClose}
              className={`ml-auto ${ICON_BUTTON}`}
            >
              <Icon kind="close" size={14} />
            </button>
          </header>
        )}
        <div className="flex-none border-b border-border-soft px-5 pb-3">
          <div className="mb-2 text-[17px] text-fg">{c.title}</div>
          <div className="flex items-center gap-[10px] text-[12px] text-body-soft">
            <div className="h-[5px] flex-1 overflow-hidden rounded-[3px] bg-border-soft">
              <div
                className="h-full bg-field-strong"
                style={{
                  width: `${total ? Math.round((done / total) * 100) : 0}%`,
                }}
              />
            </div>
            <span className="tabular-nums">
              {m.cases_graph_progress({ done, total })}
            </span>
          </div>
        </div>
        <div className="@container relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
          {/* On a phone the last rows come out above the close button. */}
          <div
            className="relative"
            style={{
              height,
              marginBottom: phone
                ? "calc(var(--dock-floor) + 65px)"
                : undefined,
            }}
          >
            {/* The ground of the current steps' rows, under the lines. */}
            {rows
              .filter((r) => r.state === "now")
              .map((r) => (
                <div
                  key={r.step.id}
                  aria-hidden="true"
                  className="absolute right-[4px] left-[4px] rounded-[10px] bg-chosen"
                  style={{
                    top: rowY(r.index, geo) - geo.rowH / 2 + 3,
                    height: geo.rowH - 6,
                  }}
                />
              ))}
            <svg
              width={svgWidth}
              height={height}
              viewBox={`0 0 ${svgWidth} ${height}`}
              aria-hidden="true"
              className="pointer-events-none absolute top-0 left-0 z-[1] block overflow-visible"
            >
              {rows.flatMap((child) =>
                child.parents.map((id) => {
                  const parent = byId.get(id);
                  if (!parent) return null;
                  return (
                    <path
                      key={`${id}-${child.step.id}`}
                      d={edgePath(parent, child, geo)}
                      fill="none"
                      strokeWidth={geo.lineWidth}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      style={{
                        stroke: lineColor(edgeColor(parent, child)),
                        strokeOpacity: child.state === "later" ? 0.25 : 1,
                      }}
                    />
                  );
                }),
              )}
              {rows.map((r) => (
                <Station
                  key={r.step.id}
                  r={r}
                  g={geo}
                  x={laneX(r.lane, geo)}
                  y={rowY(r.index, geo)}
                  still={still}
                />
              ))}
            </svg>
            {rows.map((r) => {
              const meta = metaOf(r);
              return (
                <button
                  key={r.step.id}
                  type="button"
                  data-graph-step={r.step.id}
                  data-current={r.state === "now" ? "" : undefined}
                  onClick={() => onSelect(r.step.id)}
                  // The same box as the current row's ground (reaching under
                  // the lines); the title is moved right of the lines by padding.
                  className="absolute right-[4px] left-[4px] flex cursor-pointer flex-col justify-center rounded-[10px] pr-[8px] text-left hover:bg-hover @min-[640px]:flex-row @min-[640px]:items-center @min-[640px]:justify-start @min-[640px]:gap-4"
                  style={{
                    top: rowY(r.index, geo) - geo.rowH / 2 + 3,
                    height: geo.rowH - 6,
                    paddingLeft: lefts[r.index] - 4,
                  }}
                >
                  <span
                    className={`flex min-w-0 items-center gap-[6px] text-[13.5px] @min-[640px]:flex-1 ${
                      r.state === "done"
                        ? "text-placeholder"
                        : r.state === "now"
                          ? "text-fg"
                          : "text-body-soft"
                    }`}
                  >
                    <span
                      className={`flex flex-none ${
                        r.state === "now"
                          ? "text-accent"
                          : r.state === "done"
                            ? "text-placeholder"
                            : "text-muted"
                      }`}
                    >
                      <Icon
                        kind={r.step.kind === "waiting" ? "waiting" : "action"}
                        size={14}
                      />
                    </span>
                    <span className="min-w-0 truncate">{r.step.title}</span>
                  </span>
                  {meta !== "" && (
                    <span className="mt-[2px] ml-[20px] truncate text-[10.5px] text-faint @min-[640px]:m-0 @min-[640px]:w-[260px] @min-[640px]:flex-none @min-[640px]:text-[12px]">
                      {meta}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * A station, in the color of its line. Closed: a small ring and a dot.
 * Current: a tinted halo, a ring that spreads twice, a hollow core. Later:
 * a faint hollow ring. Forks and joins look like any station (the 45 degree
 * bends already say where lines part and meet).
 */
function Station({
  r,
  g,
  x,
  y,
  still,
}: {
  r: LaneRow;
  g: Geo;
  x: number;
  y: number;
  /** Reduced motion is asked for: the ring does not spread. */
  still: boolean;
}) {
  const color = lineColor(r.color);
  if (r.state === "done")
    return (
      <g>
        <circle
          cx={x}
          cy={y}
          r={g.ringR}
          strokeWidth={g.ringW}
          style={{ fill: GROUND, stroke: color }}
        />
        <circle cx={x} cy={y} r={g.dotR} style={{ fill: color }} />
      </g>
    );
  if (r.state === "now")
    return (
      <g>
        <circle
          cx={x}
          cy={y}
          r={g.haloR}
          style={{ fill: color, fillOpacity: 0.18 }}
        />
        {!still && (
          <circle
            cx={x}
            cy={y}
            r={g.pulseR}
            fill="none"
            strokeWidth={g.pulseW}
            opacity={0}
            style={{ stroke: color }}
          >
            {/* Spreads to 1.35 times and fades, twice, then stays gone. */}
            <animate
              attributeName="r"
              values={`${g.pulseR * 0.85};${g.pulseR * 1.35}`}
              dur="1.8s"
              repeatCount="2"
              fill="freeze"
            />
            <animate
              attributeName="opacity"
              values="0.7;0"
              dur="1.8s"
              repeatCount="2"
              fill="freeze"
            />
          </circle>
        )}
        <circle
          cx={x}
          cy={y}
          r={g.coreR}
          strokeWidth={g.coreW}
          style={{ fill: GROUND, stroke: color }}
        />
      </g>
    );
  return (
    <circle
      cx={x}
      cy={y}
      r={g.ringR}
      strokeWidth={g.ringW * 1.25}
      style={{ fill: GROUND, stroke: color, strokeOpacity: 0.6 }}
    />
  );
}
